// src/pages/TermsPage.jsx
import React from "react";
import { Helmet } from "react-helmet";

const SUPPORT_EMAIL  = "artparcel.sup@gmail.com";           // TODO: replace
const COMPANY_NAME   = "ArtParcel";                          // TODO: replace if needed
const SITE_URL       = "https://artparcel.club";            // TODO: replace
const LAST_UPDATED   = "September 15, 2025";                 // Keep explicit date for clarity

const TermsPage = () => {
  return (
    <div className="bg-slate-900 text-white min-h-screen flex flex-col">
      <Helmet>
        <title>Terms of Service | {COMPANY_NAME}</title>
        <meta name="description" content={`${COMPANY_NAME} Terms of Service`} />
        <meta property="og:title" content={`Terms of Service | ${COMPANY_NAME}`} />
        <meta property="og:description" content={`${COMPANY_NAME} Terms of Service`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/terms`} />
      </Helmet>

      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <header className="mb-10 text-center">
            <h1 className="text-4xl font-bold">Terms of Service</h1>
            <p className="text-sm text-slate-400 mt-2">Last updated: {LAST_UPDATED}</p>
            <p className="text-xs text-slate-500 mt-1 italic">
              This page provides general information and is not legal advice. Consult your attorney to tailor these terms to your needs.
            </p>
          </header>

          {/* Table of Contents */}
          <nav className="mb-12 rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
            <h2 className="text-lg font-semibold mb-2">Table of Contents</h2>
            <ul className="text-sm text-slate-300 grid sm:grid-cols-2 gap-y-1 gap-x-6 list-disc list-inside">
              <li><a href="#acceptance" className="hover:text-white">1. Acceptance of Terms</a></li>
              <li><a href="#definitions" className="hover:text-white">2. Definitions</a></li>
              <li><a href="#eligibility" className="hover:text-white">3. Eligibility & Accounts</a></li>
              <li><a href="#tokens" className="hover:text-white">4. Pixel Drops & Purchases</a></li>
              <li><a href="#ownership" className="hover:text-white">5. Ownership & Licenses</a></li>
              <li><a href="#rules" className="hover:text-white">6. Community Rules</a></li>
              <li><a href="#moderation" className="hover:text-white">7. Moderation & Enforcement</a></li>
              <li><a href="#copyright" className="hover:text-white">8. Copyright Complaints</a></li>
              <li><a href="#termination" className="hover:text-white">9. Term & Termination</a></li>
              <li><a href="#disclaimers" className="hover:text-white">10. Disclaimers</a></li>
              <li><a href="#liability" className="hover:text-white">11. Limitation of Liability</a></li>
              <li><a href="#indemnity" className="hover:text-white">12. Indemnification</a></li>
              <li><a href="#changes" className="hover:text-white">13. Changes to the Service/Terms</a></li>
              <li><a href="#law" className="hover:text-white">14. Governing Law & Venue</a></li>
              <li><a href="#contact" className="hover:text-white">15. Contact</a></li>
            </ul>
          </nav>

          {/* Sections */}
          <section id="acceptance" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">1. Acceptance of Terms</h2>
            <p className="text-slate-300">
              By accessing or using {COMPANY_NAME} (the “Service”), you agree to be bound by these Terms of Service
              (“Terms”). If you do not agree, do not use the Service.
            </p>
          </section>

          <section id="definitions" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">2. Definitions</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-1">
              <li><span className="font-semibold">Canvas</span>: The shared, grid-based display within the Service.</li>
              <li><span className="font-semibold">Artwork</span>: Pixel art or images a user places onto the Canvas.</li>
              <li><span className="font-semibold">Pixel Drops</span>: The in-app virtual currency used to claim, renew, or enhance Artwork. Pixel Drops have no cash value.</li>
              <li><span className="font-semibold">User Content</span>: Any content you submit, upload, or display via the Service.</li>
            </ul>
          </section>

          <section id="eligibility" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">3. Eligibility & Accounts</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-1">
              <li>You must be at least 13 years old to use the Service (or the minimum age in your jurisdiction).</li>
              <li>You are responsible for your account credentials and all activity on your account.</li>
              <li>Provide accurate information and keep it current.</li>
            </ul>
          </section>

          <section id="tokens" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">4. Pixel Drops & Purchases</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-1">
              <li>Pixel Drops are a limited, revocable, non-transferable license to use features of the Service.</li>
              <li>Pixel Drops are not currency, have no monetary value, and cannot be redeemed for legal tender.</li>
              <li>We may offer token bundles or subscriptions through third-party processors (e.g., Stripe). You agree to their terms and fees.</li>
              <li>Except where required by law or expressly stated in our <a href="/Terms/Refunds" className="text-indigo-400 hover:text-indigo-300 underline">Refund Policy</a>, all purchases are final and non-refundable.</li>
              <li>We may change pricing, bundles, or Token mechanics at any time with prospective effect.</li>
            </ul>
          </section>

          <section id="ownership" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">5. Ownership & Licenses</h2>
            <p className="text-slate-300 mb-2">
              You retain rights in your User Content. You grant {COMPANY_NAME} a worldwide, non-exclusive, transferable,
              sublicensable, royalty-free license to host, store, reproduce, modify, adapt, publish, display, perform,
              distribute, and create derivative works of your User Content for operating, improving, promoting, and
              marketing the Service (including on social channels and showcases).
            </p>
            <p className="text-slate-300">
              You represent that you have all rights necessary to grant this license and that your User Content does not
              infringe or violate any third-party rights or laws.
            </p>
          </section>

          <section id="rules" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">6. Community Rules</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-1">
              <li>No illegal, hateful, harassing, sexually explicit, or otherwise inappropriate content.</li>
              <li>No political or religious content intended to provoke or cause division.</li>
              <li>No spam or repetitive/meaningless patterns that detract from others’ experience.</li>
              <li>No botting, multi-account abuse, or automated activity to gain unfair advantage.</li>
              <li>Respect others’ intellectual property and privacy.</li>
            </ul>
          </section>

          <section id="moderation" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">7. Moderation & Enforcement</h2>
            <ul className="text-slate-300 list-disc list-inside space-y-1">
              <li>We may remove or alter User Content, revoke Pixel Drops, or suspend/terminate accounts at our discretion for violations.</li>
              <li>Artwork may expire after a set period unless renewed; expired areas can be claimed by others.</li>
              <li>Report content via in-app tools or email us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-400 hover:text-indigo-300 underline">{SUPPORT_EMAIL}</a>.</li>
            </ul>
          </section>

          <section id="copyright" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">8. Copyright Complaints</h2>
            <p className="text-slate-300">
              If you believe content on the Service infringes your copyright, please email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-400 hover:text-indigo-300 underline">{SUPPORT_EMAIL}</a>{" "}
              with: (a) your contact info, (b) identification of the work and infringing material (URL/screenshot),
              (c) a statement under penalty of perjury that you have a good-faith belief the use is unauthorized,
              and (d) your signature. We may remove content and/or terminate repeat infringers.
            </p>
          </section>

          <section id="termination" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">9. Term & Termination</h2>
            <p className="text-slate-300">
              We may suspend or terminate access to the Service at any time with or without notice, including for
              violations of these Terms. Upon termination, your license to use the Service ends and certain
              provisions of these Terms will survive (e.g., ownership, disclaimers, limitations of liability).
            </p>
          </section>

          <section id="disclaimers" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">10. Disclaimers</h2>
            <p className="text-slate-300">
              THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR
              IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
              NON-INFRINGEMENT. WE DO NOT GUARANTEE UNINTERRUPTED OR ERROR-FREE OPERATION.
            </p>
          </section>

          <section id="liability" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">11. Limitation of Liability</h2>
            <p className="text-slate-300">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY_NAME} AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS,
              REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
            </p>
          </section>

          <section id="indemnity" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">12. Indemnification</h2>
            <p className="text-slate-300">
              You agree to defend, indemnify, and hold harmless {COMPANY_NAME} and its affiliates from and against any
              claims, liabilities, damages, losses, and expenses (including reasonable attorneys’ fees) arising from
              or related to your User Content, your use of the Service, or your violation of these Terms or applicable law.
            </p>
          </section>

          <section id="changes" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">13. Changes to the Service/Terms</h2>
            <p className="text-slate-300">
              We may modify the Service or these Terms at any time. Material changes will be posted on this page with an
              updated “Last updated” date. Continued use of the Service after changes become effective constitutes acceptance.
            </p>
          </section>

          <section id="law" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">14. Governing Law & Venue</h2>
            <p className="text-slate-300">
              These Terms are governed by the laws of <span className="italic">[Your State/Country]</span>, without regard to conflict of
              laws principles. You consent to the exclusive jurisdiction and venue of the courts located in
              <span className="italic"> [Your City, State/Country]</span>.
            </p>
          </section>

          <section id="contact" className="mb-10">
            <h2 className="text-2xl font-semibold mb-3">15. Contact</h2>
            <p className="text-slate-300">
              Questions about these Terms? Email us at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-400 hover:text-indigo-300 underline">
                {SUPPORT_EMAIL}
              </a>.
            </p>
          </section>

          {/* Back to top */}
          <div className="mt-12 flex justify-center">
            <a
              href="#top"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="text-sm text-slate-300 hover:text-white underline"
            >
              Back to top
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-900/80">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-slate-400">© {new Date().getFullYear()} {COMPANY_NAME}</div>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <a href="/Terms/Terms" className="text-slate-300 hover:text-white">Terms</a>
            <a href="/Terms/Privacy" className="text-slate-300 hover:text-white">Privacy</a>
            <a href="/Terms/Refunds" className="text-slate-300 hover:text-white">Refund Policy</a>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-slate-300 hover:text-white">{SUPPORT_EMAIL}</a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
